import { TerminalBatchAddEntityToBatchAddDto } from "src/app/features/Terminal/services/mappers/TerminalBatchAddEntityToBatchAddDto";
import { TerminalCopyEntityToCopyDto } from "src/app/features/Terminal/services/mappers/TerminalCopyEntityToCopyDto";
import { TerminalDtoToEntity } from "src/app/features/Terminal/services/mappers/TerminalDtoToEntity";
import { TerminalEntityToDto } from "src/app/features/Terminal/services/mappers/TerminalEntityToDto";
import type { TerminalBatchAddEntity } from "src/app/features/Terminal/types/TerminalBatchAddEntity";
import type { TerminalCopyEntity } from "src/app/features/Terminal/types/TerminalCopyEntity";
import type { TerminalEntity } from "src/app/features/Terminal/types/TerminalEntity";
import type { BatchAddResult } from "../../../../../components/FeatureButtons/types";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { baseAuthApi } from "../../../../../core/service/baseApi";
import type { FormMutation } from "../../../../../core/service/types/FormMutation";
import type { FormQuery } from "../../../../../core/service/types/FormQuery";
import type { Dto } from "../../../../../core/types/be/Dto";

const TerminalApi = baseAuthApi.injectEndpoints({
    endpoints: (builder) => ({
        TerminalLoad: builder.query<TerminalEntity, FormQuery>({
            providesTags: [ResourceName.TERMINALS],
            query: (queryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.TERMINALS}/${queryParams.entityId}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return TerminalDtoToEntity(response);
            },
        }),
        TerminalSave: builder.mutation<TerminalEntity, FormMutation<TerminalEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.TERMINALS}`;
                const method = "POST";

                const body = TerminalEntityToDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.TERMINALS],
        }),
        TerminalUpdate: builder.mutation<TerminalEntity, FormMutation<TerminalEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();
                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.TERMINALS}/${mutation.data.id}`;
                const method = "PUT";

                const body = TerminalEntityToDto(mutation.data);
                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.TERMINALS],
        }),
        TerminalCopy: builder.mutation<TerminalEntity, FormMutation<TerminalCopyEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.TERMINALS}/${mutation.data.id}/${FORM_ACTION.COPY}`;
                const method = "POST";

                const body = TerminalCopyEntityToCopyDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.TERMINALS],
        }),
        TerminalBatchAdd: builder.mutation<BatchAddResult, FormMutation<TerminalBatchAddEntity>>({
            query: (mutation) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.TERMINALS}/${FORM_ACTION.BATCH_ADD}`;
                const method = "POST";

                const body = TerminalBatchAddEntityToBatchAddDto(mutation.data);

                return {
                    url,
                    method,
                    body,
                };
            },
            // used to refetch data. RTK Query cache invalidation triggers refetch. table data cached in mainCache from serverApi
            invalidatesTags: ["mainCache"],
        }),
    }),
});

export const {
    useTerminalLoadQuery,
    useTerminalSaveMutation,
    useTerminalUpdateMutation,
    useTerminalCopyMutation,
    useTerminalBatchAddMutation,
} = TerminalApi;
